/**
 * 活动纯逻辑（无 React / 无 DB / 无 DOM 字符串）
 * 「地区 × 类型」两维交叉筛选；状态与排序按当前时间推导。
 * 卡片与筛选区块已改为真正的 JSX 组件（见 app/[[...slug]]/components）。
 */
import type {ActivityFacetCount, ActivityItem, ActivityStatus} from '@/common/interface/activity';

/** 两行筛选各自的「全部」tab 文案 */
export const ACTIVITY_ALL_REGION = '全部地区';
export const ACTIVITY_ALL_CATEGORY = '全部活动';

/** 本地日期 → YYYY-MM-DD（避免 toISOString 的时区偏移） */
export function ymd(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** YYYY-MM-DD → 「8月28日」；非法输入原样返回 */
export function monthDay(date: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
    return m ? `${Number(m[2])}月${Number(m[3])}日` : (date || '');
}

/** 状态 → CSS class 后缀 */
export function statusKey(st: ActivityStatus): string {
    return {报名中: 'open', 即将开始: 'soon', 进行中: 'live', 已结束: 'ended'}[st];
}

/**
 * 由日期推导状态。优先级：已结束 > 报名中 > 进行中 > 即将开始。
 * 「报名中」优先于「进行中」是有意的：多阶段赛事往往在赛程已开始时仍在收报名。
 * 日期缺失或格式非法时按「报名中」处理，不抛错。
 */
export function activityStatus(a: ActivityItem, now = new Date()): ActivityStatus {
    const today = ymd(now);
    const end = a.endDate || a.startDate;
    if (end && end < today) return '已结束';
    if (a.applyDeadline && a.applyDeadline >= today) return '报名中';
    if (a.startDate && a.startDate <= today && today <= end) return '进行中';
    if (a.applyDeadline) return '即将开始';
    return '报名中';
}

/** 未结束的按开始日期升序在前，已结束的沉底按开始日期倒序；同日期按标题稳定排序 */
export function sortActivities(list: ActivityItem[], now = new Date()): ActivityItem[] {
    const ended = (a: ActivityItem) => (activityStatus(a, now) === '已结束' ? 1 : 0);
    return [...list].sort((a, b) => {
        const ea = ended(a);
        const eb = ended(b);
        if (ea !== eb) return ea - eb;
        if (a.startDate !== b.startDate) {
            const asc = a.startDate < b.startDate ? -1 : 1;
            return ea ? -asc : asc;
        }
        return a.title.localeCompare(b.title, 'zh-Hans-CN');
    });
}

/** 按指定字段分组计数，按条数倒序 */
export function facetCounts(list: ActivityItem[], key: 'city' | 'category'): ActivityFacetCount[] {
    const map = new Map<string, number>();
    list.forEach((a) => {
        const v = a[key];
        if (v) map.set(v, (map.get(v) || 0) + 1);
    });
    return [...map.entries()]
        .map(([label, count]) => ({label, count}))
        .sort((x, y) => (y.count - x.count) || x.label.localeCompare(y.label, 'zh-Hans-CN'));
}

/** 首页活动区块最多展示的条数 */
export const HOME_ACTIVITY_LIMIT = 3;

/** 未结束的活动（报名中 / 进行中 / 即将开始），已按 sortActivities 的顺序 */
export function upcomingActivities(list: ActivityItem[], now = new Date()): ActivityItem[] {
    return sortActivities(list, now).filter((a) => activityStatus(a, now) !== '已结束');
}
